import { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  NameField,
  CategoryField,
  AcosGoalsField,
  TagsField,
  AdTypeField,
  ProductsField,
  ExcludedProductsField,
  StoreLandingPageFields,
  VideoField,
  CreativeField,
  CreativeProductsField,
  LandingPageProductsField,
  LandingPageDetailsField,
  Creative2ProductsField,
  TargetingField,
  AsinsField,
  AdvancedTargettingButton,
  CompositeBudgetStatusField,
  StoreSpotlightSubpageDialogue,
} from "./fields";
import AttributeSelector from "./AttributeSelector";
import AdvancedTargetingOptions from "./AdvancedTargetingOptions";
import { connect } from "react-redux";

import FormWizard from "./FormWizard";
import useWizard from "./useWizard";
import FormWizardPagination from "./FormWizardPagination";
import { selectModules } from "../../../../redux/selectors/modules";
import {
  AD_TYPES,
  COMPOSITE_PLAN_STEPS,
  TARGET_TYPES,
  MODERATION_STATUSES,
  GOALS,
  ALGORITHM_OPTIONS,
} from "./constants";
import AdPlanGoalMenu from "./AdPlanGoalMenu";
import AdPlanSummary from "./AdPlanSummary";
import AdPlanChildren from "./AdPlanChildren";
import { AdPlanContext } from "./AdPlanContext";
import WalmartSBFields from "./WalmartSBFields";
import { Row, Col } from "react-bootstrap";
import { Flex } from "../../../../../styled-system/jsx";
import logo from "../../../../assets/images/logo/Trellis_Logomark_color.png";
import SDTargetingOptions from "./SDTargetingOptions";
import { getSingleFormSteps, getExemptNavPaths } from "./AdPlanFormNavUtils";
import AssetSelector from "../AssetSelector/AssetSelector";
import AssetSummary from "../AssetSelector/AssetSummary";
import CreativePreview from "../AssetSelector/CreativePreview";
import { SDStoreLandingPageSelector } from "./fields/SDStoreLandingPageSelector";
import TargetCategories from "./TargetCategories";
import CampaignNames from "./fields/CampaignNames";
import { InlineBiddingPrograms } from "../../../custom/programs/bidding/InlineBiddingPrograms";
import SDAudienceTargets from "./SDAudienceTargets";
import { updateSavedCampaignNames } from "./utils";
import AdPlanUpdateAnnotationField from "./fields/AdPlanUpdateAnnotationField";
import { successToast } from "../../../../utils/toasts";
import { useSimpleCan } from "../../../../config/casl/casl";

const AdPlanForm = ({
  state,
  loading,
  onChange,
  errors,
  onShowChild,
  categories,
  dashboard = false,
  user,
  modules,
  marketPlace,
  channel,
  brandOptions,
  savedVideos,
  storeOptions,
  pageOptions,
  adId,
  isChild = false,
  parentDailyBudget,
  apiStatus,
  agreeToArchive,
  setAgreeToArchive,
  isModal,
  closeModal,
  sectionEdit,
  onUpdate,
  onCreate,
  clearForm,
  onParentUpdate,
  isValid,
  panelEditStep,
  isProcessing = false,
  isAdArchived = false,
}) => {
  const { t } = useTranslation(["advertising", "common"]);
  // Where the wizard is; FormWizard only displays and moves it. A saved plan shows the summary instead of the
  // wizard, and there its step is the section being edited.
  const wizard = useWizard();
  const [editStep, setEditStep] = useState(panelEditStep ?? null);
  const [editSubstep, setEditSubstep] = useState(panelEditStep ? 0 : null);
  const [isComposite, setIsComposite] = useState(undefined);
  const [fieldsToValidate, setFieldsToValidate] = useState([]);
  const [haveSkippedToAdvancedTargeting, setHaveSkippedToAdvancedTargeting] = useState(false);
  const [haveSkippedToNewProgramDialogue, setHaveSkippedToNewProgramDialogue] = useState(false);

  const steps = isComposite ? COMPOSITE_PLAN_STEPS : getSingleFormSteps(channel, state);
  // A step being edited from the review stays on screen until FormWizard's Next button clears editStep, wherever
  // the wizard is moved meanwhile; the paths getExemptNavPaths names leave the wizard showing its own position.
  const isEditingStep = !adId && editStep !== null && !getExemptNavPaths(state, editStep, editSubstep);
  const step = isEditingStep ? editStep : wizard.step;
  const substep = isEditingStep ? (editSubstep ?? 0) : wizard.substep;
  const review = adId || step === steps.filter((s) => !s.isHidden).length - 1;

  const userCanUpdateAds = useSimpleCan("update", "Ad") && !isAdArchived;

  const { setChildPlan, savedChildPlans, setSavedChildPlans, resetAdPlanContext } = useContext(AdPlanContext);

  const setupFields = [
    [
      <ProductsField
        key={1}
        state={state}
        onChange={onChange}
        errors={errors}
        fieldsToValidate={fieldsToValidate}
        review={review}
        isModal={isModal}
      />,
      <ExcludedProductsField
        key={"1b"}
        state={state}
        onChange={onChange}
        errors={errors}
        fieldsToValidate={fieldsToValidate}
        review={review}
        isModal={isModal}
      />,
      <LandingPageProductsField
        key={2}
        state={state}
        onChange={onChange}
        errors={errors}
        fieldsToValidate={fieldsToValidate}
        adId={adId}
        review={review}
        isModal={isModal}
      />,
      <CreativeProductsField
        key={3}
        state={state}
        onChange={onChange}
        errors={errors}
        fieldsToValidate={fieldsToValidate}
        adId={adId}
        review={review}
        isModal={isModal}
      />,
      <Creative2ProductsField
        key={4}
        state={state}
        onChange={onChange}
        errors={errors}
        fieldsToValidate={fieldsToValidate}
        adId={adId}
        review={review}
        isModal={isModal}
      />,
    ],
  ];

  const adTypeFields = [
    [
      <AdTypeField
        key={5}
        state={state}
        onChange={onChange}
        marketPlace={marketPlace}
        errors={errors}
        fieldsToValidate={fieldsToValidate}
        adId={adId}
        isChild={state.isChild}
        user={user}
        channel={channel}
        review={review}
        brandOptions={brandOptions}
        setEditStep={setEditStep}
        categories={categories}
        dashboard={dashboard}
      />,
    ],
    [
      <h2
        key={7}
        style={{
          fontWeight: 700,
          marginTop: 0,
          fontSize: "24px",
        }}
      >
        {t(($) => $.advertising["Sponsored Brand Creatives"])}
      </h2>,
      <p
        key={8}
        style={{
          fontSize: "16px",
          maxWidth: "90%",
          marginBottom: "2rem",
        }}
        translate="yes"
      >
        {t(($) => $.advertising["Select media assets for your campaign below."])}{" "}
        <span>
          {state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value ?
            t(($) => $.advertising["All fields are optional."])
          : ""}
        </span>
      </p>,
      <Col key={9} xs={12} className="pe-4">
        <CreativeField
          state={state}
          onChange={onChange}
          errors={errors}
          fieldsToValidate={fieldsToValidate}
          adId={adId}
          logoOptions={
            state.logoDetails ?
              [state.logoDetails]?.map((logo) => ({
                value: logo.asset_id,
                label: logo.name,
                url: logo.url_default,
              }))
            : []
          }
          creativesAssetSelection={true}
          review={review}
          optional={!adId && !review && state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value}
        />
        {/* SB Video details */}
        <VideoField
          state={state}
          onChange={onChange}
          errors={errors}
          fieldsToValidate={fieldsToValidate}
          adId={adId}
          brandOptions={brandOptions}
          savedVideos={savedVideos}
          review={review}
        />
        {/* Sponsored Brand Creatives Asset Selector */}
        <AssetSelector
          state={state}
          onChange={onChange}
          errors={errors}
          fieldsToValidate={fieldsToValidate}
          adId={adId}
          hidden={state.id}
          defaultPageSize={8}
          handleAssetSelection={(assetId, assetDetails, type) => {
            // Avoid conflict with SB logo treatment, need to have stored asset details for review display during creation
            const detailsKey = `${type}Details`;

            // Clear old selected creative if video or lifestyle image - only one or the other allowed in this context
            let removalKey;
            if (detailsKey === "videoDetails") {
              removalKey = "lifestyleImageDetails";
            }
            if (detailsKey === "lifestyleImageDetails") {
              removalKey = "videoDetails";
            }

            if (assetId === state.creativeAsset || assetId === null) {
              onChange({
                creativeAsset: null,
                [type]: null,
                [detailsKey]: null,
              });
            } else {
              onChange({
                creativeAsset: assetId,
                [type]: assetId,
                [detailsKey]: assetDetails,
                [removalKey]: null,
              });
            }
          }}
          selected={state.creativeAsset}
          selections={state.creativeAsset ?? []}
          allowedAssetTypes={
            state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ?
              state.storeLandingPage ?
                ["LOGO", "VIDEO"]
              : ["VIDEO"]
            : state.storeSpotlight || state.manualCollection || state.autoCollection || state.newProductCollection ?
              // This selector only renders on create, and Amazon now builds a Manual Collection
              // from a Product Collection create, which never renders a custom image.
              ["LOGO"]
            : ["LOGO", "LIFESTYLE_IMAGE", "PRODUCT_IMAGE"]
          }
          initialTypeFilter={
            state.generatedAssetNameFilter ? "LIFESTYLE_IMAGE"
            : state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ?
              "VIDEO"
            : [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_V2.value].includes(state.adType) ?
              "LOGO"
            : "LIFESTYLE_IMAGE"
          }
          review={review}
          assetDescriptor={
            state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ? t(($) => $.advertising["Selected Video Creative"])
            : !adId && !review ?
              t(($) => $.advertising["Selected Creative (optional)"])
            : t(($) => $.advertising["Selected Creative"])
          }
          hidePreviewField={
            (state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && !state.storeLandingPage) ||
            state.storeSpotlight ||
            state.manualCollection ||
            state.autoCollection ||
            state.newProductCollection
          }
          marketPlace={marketPlace}
        />
        <Row>
          <AssetSummary
            assetId={state.lifestyleImageDetails?.asset_id}
            assetDescriptor={
              state.lifestyleImageDetails?.type === "VIDEO" ?
                t(($) => $.advertising["Video Creative"])
              : t(($) => $.advertising["Lifestyle Image"])
            }
            hidden={!state.id}
            assetType={state.lifestyleImageDetails?.type}
            summaryContainerClasses={`pe-5 ${state.videoDetails?.type === "VIDEO" ? "my-0" : "my-5"}`}
            columnSpan={
              state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ?
                state.logoDetails ?
                  6
                : 12
              : 6
            }
          />
          <AssetSummary
            assetId={state.logoDetails?.asset_id}
            assetDescriptor={t(($) => $.advertising["Brand Logo"])}
            hidden={!state.id}
            assetType={state.logoDetails?.type}
            summaryContainerClasses={"ps-5 my-5"}
            columnSpan={
              state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ?
                state.logoDetails ?
                  6
                : 12
              : 6
            }
          />
        </Row>
      </Col>,
    ],
  ];

  if (
    [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(state.adType) &&
    (state.storeLandingPage || state.storeSpotlight)
  ) {
    adTypeFields.splice(1, 0, [
      <AdTypeField
        key={10}
        state={state}
        onChange={onChange}
        marketPlace={marketPlace}
        errors={errors}
        fieldsToValidate={fieldsToValidate}
        adId={adId}
        isChild={isChild}
        user={user}
        channel={channel}
        review={review}
        brandOptions={brandOptions}
        isSummaryCard={true}
        setEditStep={setEditStep}
        hidden={state.storeSpotlight}
      />,
      <h2
        key={11}
        style={{
          fontWeight: 700,
          marginTop: 0,
          fontSize: "24px",
        }}
      >
        {t(($) => $.advertising["Sponsored Brand Details"])}
      </h2>,
      <p key={12} style={{ fontSize: "16px", marginBottom: "2rem", maxWidth: "90%" }} hidden={review} translate="yes">
        {channel === "walmart" ?
          t(
            ($) =>
              $.advertising[
                "Promote your brand and products in a customized ad at the top of related Walmart product pages."
              ],
          )
        : t(
            ($) =>
              $.advertising[
                "Promote your brand and products in a customized ad at the top of related Amazon product pages."
              ],
          )
        }
      </p>,
      <p
        key={13}
        style={{ fontSize: "16px", marginBottom: "2rem", maxWidth: "90%" }}
        hidden={
          review ||
          (!state.newProductCollection &&
            !state.manualCollection &&
            !(state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && state.storeLandingPage))
        }
        translate="yes"
      >
        {t(
          ($) =>
            $.advertising[
              "To use an existing store page, select from the options below. Otherwise, you can add products for a new landing page later."
            ],
        )}
      </p>,

      <StoreLandingPageFields
        key={14}
        state={state}
        onChange={onChange}
        errors={errors}
        fieldsToValidate={fieldsToValidate}
        adId={adId}
        review={review}
        storeOptions={storeOptions}
        brandOptions={brandOptions}
        pageOptions={pageOptions}
      />,
      <StoreSpotlightSubpageDialogue
        key={15}
        state={state}
        onChange={onChange}
        errors={errors}
        fieldsToValidate={fieldsToValidate}
        adId={adId}
        review={review}
        pageOptions={pageOptions}
      />,
    ]);
  }

  const nonTrellisCampaignFlag = state.goal === GOALS.IMPORTED_ADS.value;

  let targetingFields = [];

  if (nonTrellisCampaignFlag) {
    targetingFields = [
      [
        <AdvancedTargetingOptions
          key={16}
          state={state}
          errors={errors}
          fieldsToValidate={fieldsToValidate}
          channel={channel}
          onChange={onChange}
          review={review || adId}
          isModal={isModal}
          modules={modules}
          targetingModalEdit={
            isModal &&
            ((state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value && step === 4) ||
              ([AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(
                state.adType,
              ) &&
                step === 4) ||
              (state.adType === AD_TYPES.SPONSORED_BRAND.value && step === 2 && state.storeSpotlight) ||
              step === 3)
          }
          marketPlace={marketPlace}
        />,
      ],
    ];
  } else {
    targetingFields = [
      [
        <TargetingField
          key={17}
          state={state}
          onChange={onChange}
          fieldsToValidate={fieldsToValidate}
          errors={errors}
          review={review}
          channel={channel}
          isSummaryCard={state.targetType}
        />,
        <h2
          key={18}
          style={{
            fontWeight: 500,
            fontSize: "20px",
            marginTop: "1rem",
            marginLeft: state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value ? 0 : "2rem",
            display:
              (
                review ||
                state.adType === AD_TYPES.SPONSORED_DISPLAY.value ||
                state.adType === AD_TYPES.SPONSORED_PRODUCT_AUTO.value ||
                state.adType === AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value
              ) ?
                "none"
              : "inherit",
          }}
        >
          {state.targetType === TARGET_TYPES.AUTOMATIC.value ?
            t(($) => $.advertising["Targeting Parameters (Optional)"])
          : t(($) => $.advertising["Targeting Parameters"])}
        </h2>,
        <p
          key={19}
          style={{
            fontSize: "16px",
            marginBottom: "2rem",
            marginLeft: "2rem",
            maxWidth: "90%",
          }}
          translate="yes"
          hidden={
            review ||
            state.adType === AD_TYPES.SPONSORED_DISPLAY.value ||
            state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value ||
            state.adType === AD_TYPES.SPONSORED_PRODUCT_AUTO.value ||
            state.adType === AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value
          }
        >
          {state.adType === AD_TYPES.SPONSORED_PRODUCT.value && channel !== "walmart" ?
            state.id ?
              t(
                ($) =>
                  $.advertising[
                    "If you want your targeting plan to include specific keywords or ASINs, add them here. You can add to them anytime, but keywords and ASINs already saved to this plan can't be removed."
                  ],
              )
            : t(
                ($) =>
                  $.advertising[
                    "If you want your targeting plan to include specific keywords or ASINs, add them here. You can add, change or delete them anytime."
                  ],
              )

          : state.id ?
            t(
              ($) =>
                $.advertising[
                  "If you want your targeting plan to include specific keywords, add them here. You can add to them anytime, but keywords already saved to this plan can't be removed."
                ],
            )
          : t(
              ($) =>
                $.advertising[
                  "If you want your targeting plan to include specific keywords, add them here. You can add, change or delete them anytime."
                ],
            )
          }
        </p>,
        <SDTargetingOptions
          key={20}
          state={state}
          errors={errors}
          fieldsToValidate={fieldsToValidate}
          onChange={onChange}
          // TODO: If we want to allow editing from main review panel, just pass in review below
          review={adId && isModal && step === 3 ? false : review}
        />,
        <AttributeSelector
          key={21}
          state={state}
          errors={errors}
          fieldsToValidate={fieldsToValidate}
          onChange={onChange}
          funnelTargeting={state.targetType === TARGET_TYPES.FUNNEL.value}
          review={review || adId}
          isModal={isModal}
        />,
        <TagsField
          key={22}
          state={state}
          onChange={onChange}
          channel={channel}
          errors={errors}
          fieldsToValidate={fieldsToValidate}
          review={review || adId}
        />,
        <AsinsField
          key={23}
          state={state}
          channel={channel}
          onChange={onChange}
          errors={errors}
          fieldsToValidate={fieldsToValidate}
          review={review || adId}
        />,
        <TargetCategories
          key={24}
          state={state}
          review={review}
          onChange={onChange}
          marketPlace={marketPlace}
          isModal={isModal}
          channel={channel}
        />,
        <SDAudienceTargets key={"24b"} state={state} onChange={onChange} review={review} isModal={isModal} />,
        <CampaignNames
          key={25}
          state={state}
          review={review}
          onChange={onChange}
          marketPlace={marketPlace}
          fieldsToValidate={fieldsToValidate}
          errors={errors}
          channel={channel}
          modules={modules}
        />,
        <AdvancedTargettingButton
          key={26}
          show={!adId && !review && !state.canHaveChildren}
          disabled={
            errors.targetType ||
            errors.filledAsins ||
            errors.filledTags ||
            (state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value &&
              (errors.sdLookbackPeriod || errors.sdAudienceTargets || errors.sdBidType || errors.sdCampaignType)) ||
            (state.targetType === TARGET_TYPES.MANUAL.value && errors.filledTagsOrAsins) ||
            (state.targetType === TARGET_TYPES.FUNNEL.value && errors.filledProductKeys) ||
            errors.validatingCampaignNames ||
            errors.duplicateCampaignNames
          }
          onChange={onChange}
          skipToAdvancedMenu={() => {
            if (editSubstep !== 1) {
              setEditStep(
                (
                  [
                    AD_TYPES.SPONSORED_BRAND_WALMART.value,
                    AD_TYPES.SPONSORED_DISPLAY_V2.value,
                    AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
                  ].includes(state.adType)
                ) ?
                  4
                : state.storeSpotlight && !state.newLandingPage ? 2
                : 3,
              );
              setEditSubstep(1);
            }

            // The first time advanced options are turned on, move on to them
            if (!isComposite && !haveSkippedToAdvancedTargeting) {
              setHaveSkippedToAdvancedTargeting(true);
              if (state.storeSpotlight && state.page?.value) {
                // An existing store page hides the product step, so targeting is step 2
                wizard.setStep(2);
                wizard.setSubstep(1);
              } else {
                const advancedSteps = getSingleFormSteps(channel, { ...state, renderAdvancedOptions: true });
                if (advancedSteps[step]?.substeps?.[wizard.substep + 1]) {
                  wizard.next(advancedSteps, step, wizard.substep);
                }
              }
            }
          }}
        />,
      ],
      [
        <AdvancedTargetingOptions
          key={27}
          state={state}
          errors={errors}
          fieldsToValidate={fieldsToValidate}
          channel={channel}
          onChange={onChange}
          review={review || adId}
          isModal={isModal}
          modules={modules}
          skipToRulesForm={() => {
            setEditStep(steps.findIndex((step) => step.key === "targeting"));

            // The first time a new program is started, move on to its rules form
            if (!isComposite && state.renderAdvancedOptions && !haveSkippedToNewProgramDialogue && substep < 2) {
              setHaveSkippedToNewProgramDialogue(true);
              const rulesSteps = getSingleFormSteps(channel, {
                ...state,
                createNewProgram: true,
                biddingProgram: null,
              });
              if (rulesSteps[step]?.substeps?.[wizard.substep + 1]) {
                wizard.next(rulesSteps, step, wizard.substep);
              }
            }
          }}
          targetingModalEdit={
            isModal &&
            ((state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value && step === 4) ||
              ([AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(
                state.adType,
              ) &&
                step === 4) ||
              ([AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(
                state.adType,
              ) &&
                step === 4) ||
              (state.adType === AD_TYPES.SPONSORED_BRAND.value && step === 2 && state.storeSpotlight) ||
              step === 3)
          }
          marketPlace={marketPlace}
        />,
      ],
      [
        <div key={28} className="px-5">
          <InlineBiddingPrograms
            deferProgramCreation={true}
            hidden={review || state.goal === GOALS.CUSTOM.value}
            defaultCreateProgram={true}
            modalDisplay={true}
            parentError={
              errors?.newBiddingProgram && fieldsToValidate?.includes("newBiddingProgram") ?
                errors.newBiddingProgram
              : null
            }
            onActivate={(newProgramData) => {
              const { name, start_date, end_date, template_type, ui_state, spec, rules } = newProgramData ?? {};

              const programDefinition = {
                name,
                start_date,
                end_date,
                template_type,
                ui_state,
                spec,
                rules,
                engine: "bidding",
              };

              const targetingStepIndex = steps.findIndex((step) => step.key === "targeting");
              const advancedTargetingSubstepIndex = 1;

              onChange({
                biddingProgram: programDefinition,
                createNewProgram: false,
              });

              successToast({
                message: t(($) => $.advertising["New program {name} successfully added to media plan"], { name }),
              });

              setEditStep(targetingStepIndex);
              setEditSubstep(advancedTargetingSubstepIndex);
            }}
          />
        </div>,
      ],
    ];
  }

  const renderSingleFormStep = (step) => {
    const goalMenuIndex = 0;
    const adTypeIndex = 1;
    // Store spotlight (with existing store page) has no product selection step
    const hasHiddenProductStep = state.storeSpotlight && !state.newLandingPage;
    const productSelectionIndex = hasHiddenProductStep ? null : 2;
    const targetingIndex =
      (
        [
          AD_TYPES.SPONSORED_BRAND_WALMART.value,
          AD_TYPES.SPONSORED_DISPLAY_V2.value,
          AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
        ].includes(state.adType)
      ) ?
        4
      : hasHiddenProductStep ? 2
      : 3;
    const reviewIndex =
      (
        [
          AD_TYPES.SPONSORED_BRAND_WALMART.value,
          AD_TYPES.SPONSORED_DISPLAY_V2.value,
          AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
        ].includes(state.adType)
      ) ?
        5
      : hasHiddenProductStep ? 3
      : 4;
    const walmartCreativesIndex =
      [AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(state.adType) ? 3
      : undefined;
    const creativesAssetSelectionIndex = state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value ? 3 : undefined;

    switch (step) {
      case goalMenuIndex:
        return (
          <AdPlanGoalMenu
            state={state}
            onChange={onChange}
            setIsComposite={setIsComposite}
            roas={user.preferences.acos_or_roas === "roas"}
            errors={errors}
            fieldsToValidate={fieldsToValidate}
            user={user}
            channel={channel}
            nameField={
              <NameField
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                isDisabled={!userCanUpdateAds}
              />
            }
            categoryField={
              <CategoryField
                categories={categories}
                state={state}
                dashboard={dashboard}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                review={review}
                channel={channel}
                marketPlace={marketPlace}
                isModal={isModal}
                isDisabled={!userCanUpdateAds}
              />
            }
            acosGoalsField={
              <AcosGoalsField
                state={state}
                onChange={onChange}
                marketPlace={marketPlace}
                errors={errors}
                isChild={isChild}
                parentDailyBudget={parentDailyBudget}
                roas={user.preferences.acos_or_roas === "roas"}
              />
            }
          />
        );
      case adTypeIndex:
        return (
          <>
            <div hidden={(substep > 0 && !state.storeLandingPage) || (substep > 1 && state.storeLandingPage)}>
              <h2
                style={{
                  fontWeight: 700,
                  marginTop: 0,
                  fontSize: "24px",
                }}
              >
                {t(($) => $.advertising["Choose Ad Type"])}
              </h2>
              <p
                style={{
                  fontSize: "16px",
                  maxWidth: "90%",
                  marginBottom: "2rem",
                }}
                translate="yes"
              >
                {channel === "walmart" ?
                  t(
                    ($) =>
                      $.advertising[
                        "Showcase your brand on Walmart by selecting an ad type. The ad type you choose will affect where and how your business appears on Walmart."
                      ],
                  )
                : t(
                    ($) =>
                      $.advertising[
                        "Showcase your brand on Amazon by selecting an ad type. The ad type you choose will affect where and how your business appears on Amazon."
                      ],
                  )
                }
              </p>
            </div>
            {adTypeFields[substep ?? 0]}
          </>
        );
      case productSelectionIndex:
        return (
          <>
            <h2 style={{ fontWeight: 700, marginTop: 0, fontSize: "24px" }}>
              {state.autoCollection ?
                t(($) => $.advertising["Product Exclusions"])
              : substep === 1 ?
                t(($) => $.advertising["Select your landing page products"])
              : t(($) => $.advertising["Select your products"])}
            </h2>

            <p style={{ fontSize: "16px" }} translate="yes">
              {state.autoCollection ?
                t(
                  ($) =>
                    $.advertising[
                      "Amazon automatically selects the products to feature in an auto collection ad. Any products you select below will only be excluded from being featured - you can exclude up to 1000."
                    ],
                )
              : t(($) => $.advertising["Select the products that you want to advertise in this plan."])}
            </p>
            {state.autoCollection && (
              <p style={{ fontSize: "16px" }} translate="yes">
                {t(
                  ($) =>
                    $.advertising[
                      "Exclusions work best used sparingly. The more of your catalog Amazon has to choose from, the better it can pick the products most likely to convert."
                    ],
                )}
              </p>
            )}
            {setupFields[substep ?? 0]}
          </>
        );
      case targetingIndex: {
        const substepTitles = [
          t(($) => $.advertising["Setup Targeting Plan"]),
          t(($) => $.advertising["Advanced Settings"]),
          t(($) => $.advertising["Create a New Bidding Program"]),
        ];
        return (
          <>
            <h2 style={{ fontWeight: 700, marginTop: 0, fontSize: "24px" }} className={substep === 2 ? "px-5" : ""}>
              {substepTitles[substep]}
            </h2>
            <p
              style={{
                fontSize: "16px",
                marginBottom: "1rem",
                maxWidth: "90%",
              }}
              translate="yes"
              className={substep === 2 ? "d-none" : ""}
            >
              {substep === 1 ?
                t(
                  ($) =>
                    $.advertising[
                      "We pre-selected some of your settings based on your media plan and targeting types. Modifying them is only recommended for experienced users."
                    ],
                )
              : channel === "walmart" ?
                t(($) => $.advertising["Targeting determines your ad placement based on keywords and themes."])
              : t(($) => $.advertising["Targeting determines your ad placement based on keywords, themes, and ASINs."])}
            </p>
            {/* False positive: both branches index `targetingFields`, an array of arrays of JSX
                elements. The rule cannot type a member expression. */}
            {/* oxlint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
            {!state.targetType ? targetingFields[0]?.[0] : targetingFields[substep ?? 0]}
          </>
        );
      }
      case reviewIndex:
        return (
          <AdPlanSummary
            isAdArchived={isAdArchived}
            setStep={wizard.setStep}
            setEditStep={setEditStep}
            setEditSubstep={setEditSubstep}
            acosGoalsField={
              <AcosGoalsField
                state={state}
                onChange={onChange}
                marketPlace={marketPlace}
                errors={errors}
                isChild={isChild}
                parentDailyBudget={parentDailyBudget}
                roas={user.preferences.acos_or_roas === "roas"}
              />
            }
            adTypeFields={adTypeFields}
            sbFields={[
              <LandingPageDetailsField
                key={29}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                brandOptions={brandOptions}
                review={review}
                categories={categories}
                dashboard={dashboard}
                isModal={isModal}
              />,
              <StoreLandingPageFields
                key={30}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                storeOptions={storeOptions}
                brandOptions={brandOptions}
                pageOptions={pageOptions}
                reviewChildren={
                  state.storeSpotlight ?
                    <AssetSummary
                      assetId={state.logoDetails?.asset_id}
                      assetDescriptor={t(($) => $.advertising["Brand Logo"])}
                      assetType={state.logoDetails?.type}
                      hidden={!state.logoDetails}
                      summaryContainerClasses={state.storeSpotlight ? "ps-5 mt-3" : "ps-5 my-0"}
                    />
                  : null
                }
              />,
              <VideoField
                key={31}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                brandOptions={brandOptions}
                savedVideos={savedVideos}
                review={review}
              />,
              <Row key={32}>
                <AssetSummary
                  assetId={state.videoDetails?.asset_id ?? state.lifestyleImageDetails?.asset_id ?? state.video}
                  assetDescriptor={
                    (
                      state.lifestyleImageDetails?.type === "VIDEO" ||
                      state.videoDetails?.type === "VIDEO" ||
                      state.video
                    ) ?
                      t(($) => $.advertising["Video Creative"])
                    : t(($) => $.advertising["Lifestyle Image"])
                  }
                  assetType={state.videoDetails?.type || state.video ? "VIDEO" : "IMAGE"}
                  hidden={!state.videoDetails && !state.lifestyleImageDetails && !state.video}
                  summaryContainerClasses={`pe-5 ${
                    state.videoDetails?.type === "VIDEO" && !state.landingPageProduct?.length ? "my-0" : "my-5"
                  }`}
                  columnSpan={
                    state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ?
                      state.logoDetails ?
                        6
                      : 12
                    : 6
                  }
                />
                <AssetSummary
                  assetId={state.logoDetails?.asset_id}
                  assetDescriptor={t(($) => $.advertising["Brand Logo"])}
                  assetType={state.logoDetails?.type}
                  hidden={!state.logoDetails || state.storeSpotlight}
                  summaryContainerClasses={
                    state.storeSpotlight ? `pe-5 my-0`
                      // Logo is the only asset summary, so it takes the left column slot
                    : !state.videoDetails && !state.lifestyleImageDetails && !state.video ?
                      `pe-5 my-5`
                    : `ps-5 ${
                        state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && !state.landingPageProduct?.length ?
                          "my-0"
                        : "my-5"
                      }`

                  }
                />
              </Row>,
            ]}
            videoField={[
              <LandingPageProductsField
                key={33}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                isModal={isModal}
              />,
            ]}
            productFields={[
              <ProductsField
                key={34}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                isModal={isModal}
              />,
              <LandingPageProductsField
                key={35}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                isModal={isModal}
              />,
              <ExcludedProductsField
                key={"35b"}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                isModal={isModal}
              />,
            ]}
            storeSpotlightFields={[
              <StoreSpotlightSubpageDialogue
                key={36}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                pageOptions={pageOptions}
              />,
            ]}
            creativeProductFields={[
              <CreativeProductsField
                key={37}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                isModal={isModal}
              />,
              <Creative2ProductsField
                key={38}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                isModal={isModal}
              />,
            ]}
            creativesAssetSelectionFields={
              <CreativeField
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                optional={false}
                logoOptions={
                  state.logoDetails ?
                    [state.logoDetails]?.map((logo) => ({
                      value: logo.asset_id,
                      label: logo.name,
                      url: logo.url_default,
                    }))
                  : []
                }
                review={review}
                creativesAssetSelection={true}
                isModal={isModal}
              />
            }
            targetingFields={targetingFields}
            state={state}
            onChange={onChange}
            errors={errors}
            fieldsToValidate={fieldsToValidate}
            adId={adId}
            user={user}
            channel={channel}
            review={review}
            isModal={isModal}
            categories={categories}
            marketPlace={marketPlace}
            agreeToArchive={agreeToArchive}
            setAgreeToArchive={setAgreeToArchive}
            apiStatus={apiStatus}
          />
        );
      case walmartCreativesIndex:
        return (
          <Col xs={12} lg={10}>
            <WalmartSBFields
              state={state}
              errors={errors}
              fieldsToValidate={fieldsToValidate}
              onChange={onChange}
              adId={adId}
              review={review || adId}
              isModal={isModal}
            />
          </Col>
        );
      case creativesAssetSelectionIndex:
        return (
          <Col xs={12} className="pe-4">
            <h2
              style={{
                fontWeight: 700,
                marginTop: 0,
                fontSize: "24px",
              }}
            >
              {t(($) => $.advertising["Sponsored Display Creatives"])}
            </h2>
            <p
              style={{
                fontSize: "16px",
                maxWidth: "90%",
                marginBottom: "2rem",
              }}
              translate="yes"
            >
              {t(($) => $.advertising["Select media assets for your campaign below."])}{" "}
              {t(($) => $.advertising["All fields are optional."])}
            </p>
            <CreativeField
              state={state}
              onChange={onChange}
              errors={errors}
              fieldsToValidate={fieldsToValidate}
              adId={adId}
              optional={!state.id && state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value}
              logoOptions={
                state.logoDetails ?
                  [state.logoDetails]?.map((logo) => ({
                    value: logo.asset_id,
                    label: logo.name,
                    url: logo.url_default,
                  }))
                : []
              }
              review={review}
              creativesAssetSelection={true}
              isModal={isModal}
            />
            <AssetSelector
              state={state}
              onChange={onChange}
              errors={errors}
              fieldsToValidate={fieldsToValidate}
              adId={adId}
              onUpdate={async () => {
                if (!state.errors) {
                  await onUpdate(state);
                  closeModal();
                }
              }}
              closeModal={closeModal}
              hidden={
                (state.id && state.adType !== AD_TYPES.SPONSORED_DISPLAY_V2.value) ||
                (state.id &&
                  isModal &&
                  state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value &&
                  state.advModerationStatus === MODERATION_STATUSES.PENDING.value)
              }
              defaultPageSize={8}
              handleAssetSelection={(assetId, assetDetails, type) => {
                if (type === "logo") {
                  onChange({ logo: assetId });
                }
                // Avoid conflict with SB logo treatment, need to have stored asset details for review display during creation
                type += "Details";
                // Clear old selected creative if video or lifestyle image - only one or the other allowed in this context
                let removalKey;
                if (type === "videoDetails") {
                  removalKey = "lifestyleImageDetails";
                }
                if (type === "lifestyleImageDetails") {
                  removalKey = "videoDetails";
                }

                if (assetId === state.creativeAsset || assetId === null) {
                  onChange({ creativeAsset: null, [type]: null });
                } else {
                  const updatedCreativeFields = {
                    creativeAsset: assetId,
                    selectedType: type,
                    [type]: assetDetails,
                    [removalKey]: null,
                  };
                  if (type === "lifestyleImageDetails") {
                    updatedCreativeFields["sdSquareImageCroppingCoordinates"] = {};
                    updatedCreativeFields["sdRectangleImageCroppingCoordinates"] = {};
                  }

                  if (type === "logoDetails") {
                    updatedCreativeFields["logo"] = assetId;
                  }
                  onChange(updatedCreativeFields);
                }
              }}
              selected={state.creativeAsset}
              selections={state.creativeAsset ?? []}
              allowedAssetTypes={["LIFESTYLE_IMAGE", "PRODUCT_IMAGE", "LOGO", "VIDEO"]}
              review={review}
              initialTypeFilter={
                state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ? "VIDEO"
                : state.adType === AD_TYPES.SPONSORED_BRAND.value ?
                  "LOGO"
                : "LIFESTYLE_IMAGE"
              }
              marketPlace={marketPlace}
            />
            <Row>
              <AssetSummary
                assetId={state.creativeAsset ?? state.video}
                assetDescriptor={
                  state.lifestyleImageDetails?.type === "VIDEO" || state.video ?
                    t(($) => $.advertising["Video Creative"])
                  : t(($) => $.advertising["Lifestyle Image"])
                }
                hidden={
                  !state.id ||
                  (state.id && !state.lifestyleImageDetails && !state.creativeAsset && !state.video) ||
                  (state.id &&
                    isModal &&
                    state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value &&
                    state.advModerationStatus !== MODERATION_STATUSES.PENDING.value)
                }
                assetType={state.videoDetails?.type || state.video ? "VIDEO" : "IMAGE"}
                summaryContainerClasses={`pe-5 ${
                  state.videoDetails?.type === "VIDEO" ?
                    review && !state.id ?
                      "mt-0 mb-5"
                    : "my-0"
                  : "my-5"
                }`}
                columnSpan={
                  state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ?
                    state.logoDetails ?
                      6
                    : 12
                  : 6
                }
              />
              <AssetSummary
                assetId={state.logo}
                assetDescriptor={t(($) => $.advertising["Brand Logo"])}
                hidden={
                  !state.id ||
                  (state.id && !state.logoDetails) ||
                  (state.id &&
                    isModal &&
                    state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value &&
                    state.advModerationStatus !== MODERATION_STATUSES.PENDING.value)
                }
                assetType={state.logoDetails?.type}
                summaryContainerClasses={"ps-5 my-5"}
                columnSpan={
                  state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ?
                    state.logoDetails ?
                      6
                    : 12
                  : 6
                }
              />
            </Row>
            {(!review || state.customSdStorePageEnabled) && (
              <Row className="mt-4">
                <Col xs={12}>
                  <SDStoreLandingPageSelector
                    brandEntityId={state.brand?.value}
                    categoryId={state.categoryId ?? undefined}
                    store={state.store ?? null}
                    page={state.page ?? null}
                    hasRequiredCreatives={
                      !!(state.logoDetails && (state.lifestyleImageDetails || state.videoDetails) && state.headline)
                    }
                    customSdStorePageEnabled={!!state.customSdStorePageEnabled}
                    onChange={onChange}
                    disableFields={review}
                    errors={{
                      customSdStorePageEnabled: errors.customSdStorePageEnabled,
                      store: errors.store,
                      page: errors.page,
                    }}
                  />
                </Col>
              </Row>
            )}
            <Row
              style={{
                display:
                  state.id && state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value && !isModal ? "inherit" : "none",
              }}
            >
              <CreativePreview
                state={state}
                // Creative preview endpoint doesn't return HTML consistently with more than 1 ASIN, for now just include first selected
                productId={state.product?.[0]}
                marketPlace={marketPlace?.marketPlace}
                showCreativePreview={state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value}
                accordionLayout={false}
              />
            </Row>
          </Col>
        );
      default:
        return;
    }
  };

  const renderCompositeFormStep = (step) => {
    const goalMenuIndex = 0;
    const productSelectionIndex = 1;
    const componentTableIndex = 2;
    const componentTargetingIndex = 3;
    const reviewIndex = 4;

    switch (step) {
      case goalMenuIndex:
        return (
          <AdPlanGoalMenu
            state={state}
            onChange={onChange}
            setIsComposite={setIsComposite}
            roas={user.preferences.acos_or_roas === "roas"}
            errors={errors}
            fieldsToValidate={fieldsToValidate}
            user={user}
            channel={channel}
            nameField={
              <NameField
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                isDisabled={!userCanUpdateAds}
              />
            }
            acosGoalsField={
              <AcosGoalsField
                state={state}
                onChange={onChange}
                marketPlace={marketPlace}
                errors={errors}
                isChild={isChild}
                parentDailyBudget={parentDailyBudget}
                roas={user.preferences.acos_or_roas === "roas"}
              />
            }
          />
        );
      case productSelectionIndex:
        return (
          <>
            <h2 style={{ fontWeight: 700, marginTop: 0, fontSize: "24px" }}>
              {t(($) => $.advertising["Choose Products"])}
            </h2>

            <p style={{ fontSize: "16px" }} translate="yes">
              {t(($) => $.advertising["Select the products that you want to advertise in this plan."])}
            </p>

            {setupFields}
          </>
        );
      case componentTableIndex:
        return (
          <>
            {[
              <AdPlanChildren
                key={39}
                state={state}
                children={savedChildPlans ?? []}
                adId={adId}
                marketPlace={marketPlace}
                errors={errors}
                categoryId={state.categoryId}
                sectionEdit={sectionEdit}
                onChange={onChange}
                fieldsToValidate={fieldsToValidate}
                modules={modules}
                roas={user.preferences.acos_or_roas === "roas"}
              />,
            ]}
          </>
        );
      case componentTargetingIndex:
        return <>{targetingFields}</>;
      case reviewIndex:
        return (
          <>
            {
              [
                <AdPlanSummary
                  key={40}
                  isAdArchived={isAdArchived}
                  setStep={wizard.setStep}
                  setEditStep={setEditStep}
                  setEditSubstep={setEditSubstep}
                  acosGoalsField={[
                    <AcosGoalsField
                      key={41}
                      state={state}
                      onChange={onChange}
                      marketPlace={marketPlace}
                      errors={errors}
                      isChild={isChild}
                      parentDailyBudget={parentDailyBudget}
                      roas={user.preferences.acos_or_roas === "roas"}
                    />,
                    <CompositeBudgetStatusField key={42} state={state} onChange={onChange} />,
                  ]}
                  adTypeFields={adTypeFields}
                  productFields={[
                    <ProductsField
                      key={43}
                      state={state}
                      onChange={onChange}
                      errors={errors}
                      fieldsToValidate={fieldsToValidate}
                      adId={adId}
                      review={review}
                      isModal={isModal}
                    />,
                  ]}
                  targetingFields={targetingFields}
                  adPlanChildrenSummaryField={
                    <AdPlanChildren
                      state={state}
                      children={state.children ?? []}
                      onShowChild={onShowChild}
                      adId={adId}
                      marketPlace={marketPlace}
                      errors={errors}
                      categoryId={state.categoryId}
                      sectionEdit={sectionEdit}
                      onChange={onChange}
                      fieldsToValidate={fieldsToValidate}
                      review={true}
                      modules={modules}
                      roas={user.preferences.acos_or_roas === "roas"}
                    />
                  }
                  state={state}
                  onChange={onChange}
                  onParentUpdate={onParentUpdate}
                  errors={errors}
                  fieldsToValidate={fieldsToValidate}
                  adId={adId}
                  user={user}
                  channel={channel}
                  review={review}
                  categories={categories}
                  marketPlace={marketPlace}
                  agreeToArchive={agreeToArchive}
                  setAgreeToArchive={setAgreeToArchive}
                  apiStatus={apiStatus}
                  isModal={isModal}
                />,
              ][substep]
            }
          </>
        );
      default:
        return;
    }
  };

  if (loading) {
    return (
      <Row>
        <Col
          xs={12}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <img src={logo} alt={t(($) => $.common["Loading..."])} className="rotate" width="64" />
        </Col>
      </Row>
    );
  }

  // Update/review screen for existing ads
  return (
    <div style={{ minHeight: "700px" }}>
      {adId ?
        <Flex direction="column" gap="xlarge" padding={"24px"}>
          <AdPlanSummary
            isAdArchived={isAdArchived}
            setStep={wizard.setStep}
            setEditStep={setEditStep}
            setEditSubstep={setEditSubstep}
            acosGoalsField={[
              <AcosGoalsField
                key={44}
                state={state}
                onChange={onChange}
                marketPlace={marketPlace}
                errors={errors}
                isChild={isChild}
                parentDailyBudget={parentDailyBudget}
                roas={user.preferences.acos_or_roas === "roas"}
                isDisabled={!userCanUpdateAds}
              />,
              <CompositeBudgetStatusField key={45} state={state} onChange={onChange} isDisabled={!userCanUpdateAds} />,
            ]}
            sbFields={[
              <LandingPageDetailsField
                key={46}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                brandOptions={brandOptions}
                review={review}
                categories={categories}
                dashboard={dashboard}
                isModal={isModal}
                isDisabled={!userCanUpdateAds}
              />,
              <StoreLandingPageFields
                key={47}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                storeOptions={storeOptions}
                brandOptions={brandOptions}
                pageOptions={pageOptions}
                isDisabled={!userCanUpdateAds}
              />,
              <CreativeField
                key={48}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                logoOptions={
                  state.logoDetails ?
                    [state.logoDetails]?.map((logo) => ({
                      value: logo.asset_id,
                      label: logo.name,
                      url: logo.url_default,
                    }))
                  : []
                }
                review={review}
                optional={!adId && !review && state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value}
                isModal={isModal}
                isDisabled={!userCanUpdateAds}
              />,
              <Row key={49}>
                <AssetSummary
                  assetId={state.videoDetails?.asset_id ?? state.lifestyleImageDetails?.asset_id ?? state.video}
                  assetDescriptor={
                    (
                      state.lifestyleImageDetails?.type === "VIDEO" ||
                      state.videoDetails?.type === "VIDEO" ||
                      state.video
                    ) ?
                      t(($) => $.advertising["Video Creative"])
                    : t(($) => $.advertising["Lifestyle Image"])
                  }
                  assetType={
                    state.lifestyleImageDetails?.type === "IMAGE" ? "IMAGE"
                    : state.videoDetails || state.video ?
                      "VIDEO"
                    : null
                  }
                  hidden={!state.lifestyleImageDetails && !state.video}
                  summaryContainerClasses={`pe-5 ${
                    state.videoDetails?.type === "VIDEO" ?
                      review && !state.id ?
                        "mt-0 mb-5"
                      : "my-0"
                    : "my-5"
                  }`}
                  columnSpan={
                    state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ?
                      state.logoDetails ?
                        6
                      : 12
                    : 6
                  }
                />
                <AssetSummary
                  assetId={state.logoDetails?.asset_id ?? state.logo}
                  assetDescriptor={t(($) => $.advertising["Brand Logo"])}
                  assetType={state.logoDetails?.type}
                  hidden={!state.logoDetails && !state.logo}
                  summaryContainerClasses={
                    !state.videoDetails && !state.lifestyleImageDetails && !state.video ?
                      `pe-5 ${state.storeSpotlight ? "mb-5" : "my-5"}`
                    : `ps-5 ${state.videoDetails?.type === "VIDEO" ? "my-0" : "my-5"}`
                  }
                  columnSpan={
                    state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ?
                      state.logoDetails ?
                        6
                      : 12
                    : 6
                  }
                />
              </Row>,
            ]}
            videoField={[
              <VideoField
                key={50}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                brandOptions={brandOptions}
                savedVideos={savedVideos}
                review={review}
                isDisabled={!userCanUpdateAds}
              />,
              <LandingPageProductsField
                key={51}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                isModal={isModal}
              />,
            ]}
            adTypeFields={adTypeFields}
            productFields={[
              <ProductsField
                key={52}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                isModal={isModal}
              />,
              <ExcludedProductsField
                key={"52b"}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                isModal={isModal}
              />,
              <LandingPageProductsField
                key={53}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                isModal={isModal}
              />,
            ]}
            creativeProductFields={[
              <CreativeProductsField
                key={54}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                isModal={isModal}
              />,
              <Creative2ProductsField
                key={55}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                isModal={isModal}
              />,
            ]}
            storeSpotlightFields={[
              <StoreSpotlightSubpageDialogue
                key={56}
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                review={review}
                pageOptions={pageOptions}
              />,
            ]}
            targetingFields={targetingFields}
            adPlanChildrenSummaryField={
              <AdPlanChildren
                state={state}
                children={state.children ?? []}
                onShowChild={onShowChild}
                adId={adId}
                marketPlace={marketPlace}
                errors={errors}
                categoryId={state.categoryId}
                sectionEdit={sectionEdit}
                onChange={onChange}
                fieldsToValidate={fieldsToValidate}
                review={true}
                modules={modules}
                roas={user.preferences.acos_or_roas === "roas"}
                isDisabled={!userCanUpdateAds}
              />
            }
            creativesAssetSelectionFields={
              <CreativeField
                state={state}
                onChange={onChange}
                errors={errors}
                fieldsToValidate={fieldsToValidate}
                adId={adId}
                optional={!adId && !review && state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value}
                logoOptions={
                  state.logoDetails ?
                    [state.logoDetails]?.map((logo) => ({
                      value: logo.asset_id,
                      label: logo.name,
                      url: logo.url_default,
                    }))
                  : []
                }
                review={review}
                creativesAssetSelection={true}
                isModal={isModal}
                isDisabled={!userCanUpdateAds}
              />
            }
            state={state}
            onChange={onChange}
            onParentUpdate={onParentUpdate}
            errors={errors}
            fieldsToValidate={fieldsToValidate}
            adId={adId}
            user={user}
            channel={channel}
            review={review}
            categories={categories}
            marketPlace={marketPlace}
            agreeToArchive={agreeToArchive}
            setAgreeToArchive={setAgreeToArchive}
            apiStatus={apiStatus}
            isModal={isModal}
          />
          {state.id && userCanUpdateAds ?
            <AdPlanUpdateAnnotationField state={state} onChange={onChange} />
          : null}
          {state.id && (
            <FormWizardPagination
              onPrevious={() => {}}
              onPreviousText={t(($) => $.common.actions.Cancel)}
              onNext={async () => {
                if (!state.errors) {
                  await onUpdate(state);
                  onChange({
                    adUpdateAnnotation: "",
                    filledTargetCategories: state.filledTargetCategories?.map((c) => ({
                      ...c,
                      savedToServer: true,
                    })),
                  });
                  updateSavedCampaignNames(state.campaignNames, onChange);
                }
              }}
              onNextText={t(($) => $.advertising["Update"])}
              isPanelReview={true}
              disableUpdateButton={
                errors?.allocation ||
                errors?.dailyBudget ||
                errors?.targetACOS ||
                errors?.maxACOS ||
                (state.manualHarvestingEnabled && !state.harvestingConversions) ||
                (state.algorithm === ALGORITHM_OPTIONS.RULES_ENGINE_ONLY.value && !state.biddingProgram) ||
                errors?.validatingCampaignNames ||
                errors?.duplicateCampaignNames ||
                state.servingStatus === "ENDED" ||
                !userCanUpdateAds
              }
            />
          )}
        </Flex>
      : <FormWizard
          state={state}
          errors={errors}
          isValid={isValid}
          isModal={isModal}
          closeModal={() => {
            closeModal();
            setSavedChildPlans(null);
            setChildPlan(null);
          }}
          setFieldsToValidate={(fields) => setFieldsToValidate(fields)}
          wizard={wizard}
          step={step}
          isProcessing={isProcessing}
          editStep={editStep}
          setEditStep={setEditStep}
          formSteps={steps}
          onCreate={onCreate}
          onUpdate={onUpdate}
          clearForm={clearForm}
          onCancel={() => setIsComposite(undefined)}
          completeText={state.id ? t(($) => $.advertising["Update"]) : t(($) => $.advertising["Activate"])}
          formToRender={isComposite ? renderCompositeFormStep(step) : renderSingleFormStep(step)}
          isChild={isChild}
          saveChild={false}
          reset={resetAdPlanContext}
          wizardContext={"media-plan"}
        />
      }
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
  modules: selectModules(state),
  marketPlace: state.marketPlace,
});

export default connect(mapStateToProps)(AdPlanForm);
